import { Routes, Route, useLocation } from 'react-router-dom';
import { AppShell } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Providers } from './components/Providers';
import { Header } from './components/Header';
import { MobileNav } from './components/MobileNav';
import { Footer } from './components/Footer';
import { FreeShippingBanner } from './components/FreeShippingBanner';
import GoogleAnalytics from './components/GoogleAnalytics';
import './styles/globals.css';
import { useEffect } from 'react';

// Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductPage from './pages/ProductPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import ContactPage from './pages/ContactPage';
import AboutUsPage from './pages/AboutUsPage';
import FaqPage from './pages/FaqPage';
import ShippingPage from './pages/ShippingPage';
import ReturnsPage from './pages/ReturnsPage';

// Admin pages
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminProductEditPage from './pages/admin/AdminProductEditPage';
import AdminProductNewPage from './pages/admin/AdminProductNewPage';
import AdminOrderDetailPage from './pages/admin/AdminOrderDetailPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminDebugPage from './pages/admin/AdminDebugPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';

function AppContent() {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  const location = useLocation();

  // Close mobile nav on route change
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Check if we're on an admin page (don't show regular header/footer)
  const isAdminPage = location.pathname.startsWith('/xyz') || location.pathname.startsWith('/admin');

  if (isAdminPage) {
    return (
      <div className="App">
        <GoogleAnalytics />
        <Routes>
          {/* Admin routes */}
          <Route path="/xyz" element={<AdminDashboardPage />} />
          <Route path="/xyz/login" element={<AdminLoginPage />} />
          <Route path="/xyz/debug" element={<AdminDebugPage />} />
          <Route path="/xyz/orders" element={<AdminOrdersPage />} />
          <Route path="/xyz/orders/:id" element={<AdminOrderDetailPage />} />
          <Route path="/xyz/products/new" element={<AdminProductNewPage />} />
          <Route path="/xyz/products/:id" element={<AdminProductEditPage />} />
          <Route path="/xyz/products" element={<AdminProductsPage />} />
          <Route path="/xyz/categories" element={<AdminCategoriesPage />} />
          <Route path="/xyz/settings" element={<AdminSettingsPage />} />
          <Route path="/admin/orders" element={<AdminOrdersPage />} />
        </Routes>
      </div>
    );
  }

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 280,
        breakpoint: 'sm',
        collapsed: { desktop: true, mobile: !mobileOpened },
      }}
      padding={0}
      styles={{
        main: {
          minHeight: 'calc(100vh - 60px)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Top banner above header */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200 }}>
        <FreeShippingBanner />
      </div>

      <AppShell.Header
        style={{
          top: '41px', // Height of the banner
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Header mobileOpened={mobileOpened} toggleMobile={toggleMobile} />
      </AppShell.Header>

      <AppShell.Navbar
        style={{
          top: '101px', // Banner height + Header height
        }}
      >
        <MobileNav onClose={closeMobile} />
      </AppShell.Navbar>

      <AppShell.Main>
        <GoogleAnalytics />
        <div style={{ flex: 1, paddingTop: '41px' }}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-confirmation/:id" element={<OrderConfirmationPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/shipping" element={<ShippingPage />} />
            <Route path="/returns" element={<ReturnsPage />} />
            <Route path="/led-lights" element={<HomePage />} />
          </Routes>
        </div>
        <Footer />
      </AppShell.Main>
    </AppShell>
  );
}

function App() {
  return (
    <Providers>
      <AppContent />
    </Providers>
  );
}

export default App;
